import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

/**
 * Structured editor for `x-uioptions` when `x-uitype === 'file-upload'`.
 *
 * Production shape (audit of admin_buildex_dev real DEs):
 *   {
 *     minItems: number,
 *     maxItems: number,
 *     allowedExtensions: string[],   // each starts with "."
 *     allowedSizePerFileInMB: number,
 *   }
 *
 * Local state holds raw strings for the numeric inputs so the user can
 * temporarily clear a field without it snapping back to the previous
 * value. We coerce on emit. The extensions input is a single
 * comma-separated text box — simple to author and matches the existing
 * "enum values" convention elsewhere in the form
 * (dsv-update-schema.jsx splits enumValues on ',').
 */

function valueToState(value) {
	const v = (value && typeof value === 'object' && !Array.isArray(value)) ? value : {};
	return {
		minItems: v.minItems !== undefined && v.minItems !== null ? String(v.minItems) : '',
		maxItems: v.maxItems !== undefined && v.maxItems !== null ? String(v.maxItems) : '',
		allowedExtensions: Array.isArray(v.allowedExtensions)
			? v.allowedExtensions.join(', ')
			: '',
		allowedSizePerFileInMB: v.allowedSizePerFileInMB !== undefined && v.allowedSizePerFileInMB !== null
			? String(v.allowedSizePerFileInMB)
			: '',
		// Preserve any extra keys the user authored beyond the four we
		// expose, so a save without edits doesn't drop them.
		extras: Object.fromEntries(
			Object.entries(v).filter(([k]) => !['minItems', 'maxItems', 'allowedExtensions', 'allowedSizePerFileInMB'].includes(k)),
		),
	};
}

function normalizeExtension(raw) {
	const trimmed = String(raw).trim();
	if (trimmed === '') return '';
	return trimmed.startsWith('.') ? trimmed : `.${trimmed}`;
}

function stateToValue(state) {
	const out = { ...state.extras };
	if (state.minItems !== '') {
		const n = Number(state.minItems);
		if (Number.isFinite(n)) out.minItems = n;
	}
	if (state.maxItems !== '') {
		const n = Number(state.maxItems);
		if (Number.isFinite(n)) out.maxItems = n;
	}
	const exts = String(state.allowedExtensions || '')
		.split(',')
		.map((s) => normalizeExtension(s))
		.filter(Boolean);
	if (exts.length > 0) out.allowedExtensions = exts;
	if (state.allowedSizePerFileInMB !== '') {
		const n = Number(state.allowedSizePerFileInMB);
		if (Number.isFinite(n)) out.allowedSizePerFileInMB = n;
	}
	return Object.keys(out).length > 0 ? out : null;
}

export default function FileUploadOptionsForm({ value, onChange, disabled = false }) {
	const [state, setState] = useState(() => valueToState(value));
	const lastSeenValueRef = useRef(value);

	useEffect(() => {
		if (value === lastSeenValueRef.current) return;
		const ourLastEmit = stateToValue(state);
		const sameAsState = JSON.stringify(ourLastEmit) === JSON.stringify(value);
		lastSeenValueRef.current = value;
		if (!sameAsState) {
			setState(valueToState(value));
		}
	}, [value, state]);

	const commit = (nextState) => {
		setState(nextState);
		const next = stateToValue(nextState);
		lastSeenValueRef.current = next;
		onChange(next);
	};

	const update = (key, raw) => {
		commit({ ...state, [key]: raw });
	};

	return (
		<div className="dsv-cv-form" data-testid="rh-file-upload">
			<div className="dsv-modal__field-row">
				<div className="dsv-modal__field dsv-modal__field--half">
					<label className="dsv-modal__label" htmlFor="rh-file-min">Min files</label>
					<input
						id="rh-file-min"
						className="dsv-modal__input"
						type="number"
						min="0"
						value={state.minItems}
						disabled={disabled}
						onChange={(e) => update('minItems', e.target.value)}
						placeholder="e.g. 1"
					/>
				</div>
				<div className="dsv-modal__field dsv-modal__field--half">
					<label className="dsv-modal__label" htmlFor="rh-file-max">Max files</label>
					<input
						id="rh-file-max"
						className="dsv-modal__input"
						type="number"
						min="0"
						value={state.maxItems}
						disabled={disabled}
						onChange={(e) => update('maxItems', e.target.value)}
						placeholder="e.g. 1"
					/>
				</div>
			</div>
			<div className="dsv-modal__field">
				<label className="dsv-modal__label" htmlFor="rh-file-exts">Allowed file types</label>
				<input
					id="rh-file-exts"
					className="dsv-modal__input"
					type="text"
					value={state.allowedExtensions}
					disabled={disabled}
					onChange={(e) => update('allowedExtensions', e.target.value)}
					placeholder=".pdf, .png, .jpg"
				/>
				<p className="dsv-promote__hint" style={{ marginTop: 4 }}>
					Comma-separated. A leading dot is added automatically if you forget.
				</p>
			</div>
			<div className="dsv-modal__field">
				<label className="dsv-modal__label" htmlFor="rh-file-size">Max size per file (MB)</label>
				<input
					id="rh-file-size"
					className="dsv-modal__input"
					type="number"
					min="0"
					value={state.allowedSizePerFileInMB}
					disabled={disabled}
					onChange={(e) => update('allowedSizePerFileInMB', e.target.value)}
					placeholder="e.g. 10"
				/>
			</div>
		</div>
	);
}

FileUploadOptionsForm.propTypes = {
	value: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};
